import { useEffect, useRef, useState } from "react";
import { Button, message } from "antd";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import CustomInputField from "../../../../../../../CustomHTMLElements/CustomInputField";
import { AllPartnershipBusinessSteps } from "../../..";
import useAllGender from "../../../../../../../../custom-hooks/useAllGender";
import useWorldCountries from "../../../../../../../../custom-hooks/useWorldCountries";
import useCountryStates from "../../../../../../../../custom-hooks/useCountryStates";
import { useIdentityVerificationTypes } from "../../../../../hooks/useIdentityVerificationTypes";
import useUtilityBillTypes from "../../../../../../../../custom-hooks/useUtilityBillTypes";
import {
  postFormDataWithDotNet,
  putFormDataWithDotNet,
} from "../../../../../../../../newApis/dotNetApiMethods";
import * as ajaxEndpoints from "../../../../../../../../api/ajax-endpoints";
import ErrorHandler from "../../../../../../../ErrorHandler/ErrorHandler.component";
import {
  DocumentType,
  CustomerPeopleTypes,
  BusinessTypes,
} from "../../../../../../../../interfaces/businessSignUp";
import BusinessOwnersList from "../../../../../Components/BusinessOwnersList";
import Loader from "../../../../../../../../NewComponents/Loader/Loader.component";
import useCustomerPeopleDetails, {
  TPeopleDetails,
} from "../../../../../hooks/useCustomerPeopleDetails";
import { ICorporateCustomer } from "../../../../../hooks/useCorporateCustomers";
import { errorHandler } from "../../../../../../../../helpers/errorHandler";
import { getMaxDateOfBirth } from "../../../../../../../../helpers/validators";

import styles from "../../../Partnership.module.scss";

const schema = yup.object().shape({
  address: yup
    .string()
    .required("Please enter address")
    .min(1, "Address is required")
    .max(128),
  countryCode: yup
    .string()
    .required("Please choose country")
    .min(1, "Country is required")
    .max(128),
  city: yup
    .string()
    .required("Please enter your city name")
    .min(1, "City is required")
    .max(128),
  state: yup
    .string()
    .required("Please enter your state name")
    .min(1, "State is required")
    .max(128),
  firstName: yup.string().required("Please enter first name").min(1).max(128),
  lastName: yup.string().required("Please enter last name").min(1).max(128),
  gender: yup.string().required("Please select gender").min(1).max(128),
  dateOfBirth: yup.string().required("Please select dob").min(1).max(128),
  identityNumber: yup.string().required("Please enter identity number"),
  govtIdentityType: yup.string().required("Please select identification type"),
  email: yup
    .string()
    .email("Please enter a valid email")
    .required("Email is required"),
  phone_number: yup
    .string()
    .required("Please enter phone number")
    .matches(/^\d+$/, "Must contain only numbers"),
  utilityBill: yup.string().required("Please select proof of address"),
  idDocument: yup.string().required("Please select ID"),
});

interface IAddBusinessOwner {
  handleProceed: (step: number) => void;
  formData: any;
  resumeCustomerData?: ICorporateCustomer;
}

const AddBusinessOwner: React.FC<IAddBusinessOwner> = ({
  handleProceed,
  formData,
  resumeCustomerData,
}) => {
  const [isPickingDocument, setIsPickingDocument] = useState(false);
  const [identityTypeFile, setIdentityTypeFile] = useState<File | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const [selectedBusinessOwner, setSelectedBusinessOwner] =
    useState<TPeopleDetails>();
  const [utilityBillFile, setUtilityBillFile] = useState<File | undefined>();

  const formRef = useRef<HTMLFormElement>(null);
  const idDocumenTypeRef = useRef<HTMLInputElement>(null);
  const UtilityFileInputRef = useRef<null | HTMLInputElement>(null);

  const { data: gendersData } = useAllGender(selectedCountryCode || "NG");
  const { data: identityTypesData } = useIdentityVerificationTypes();
  const {
    data: customerPeopleDetailsData,
    refetch: refetchCustomerPeopleDetails,
    isLoading: isLoadingBusinessOwner,
  } = useCustomerPeopleDetails({
    funderPeopleType: CustomerPeopleTypes.PARTNER,
    customerId: formData?.funder_id || resumeCustomerData?.customer_id,
  });
  const { data: countriesData, isLoading: isLoadingCountries } =
    useWorldCountries(1);
  const { data: utilityBillTypesData, isLoading: isLoadingUtilityBillType } =
    useUtilityBillTypes();

  // Extract data from responses
  const genders = gendersData?.data || [];
  const identityTypes = identityTypesData?.data || [];
  const customerPeopleDetails = customerPeopleDetailsData?.data || [];
  const countries = countriesData?.data || [];
  const utilityBillTypes = utilityBillTypesData?.data || [];

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    watch,
  } = useForm<any>({
    resolver: yupResolver(schema),
  });

  const countryCode = watch("countryCode");
  const idDocumentValue = watch("idDocumentType");
  const utilityBillFileName = watch("utilityBillName");

  const resolveCountryInfo = countries?.find(
    ({ iso2 }: { iso2: string }) => iso2 === countryCode
  );
  const { data: statesData, isLoading: isStatesLoading } = useCountryStates(
    resolveCountryInfo?.id || 0
  );
  const states = statesData?.data || [];

  const onChangeUtilityBill = async (e: any) => {
    if (isPickingDocument) return;
    setIsPickingDocument(true);

    try {
      const file = e.target.files?.[0];
      if (!file) {
        setIsPickingDocument(false);
        return;
      }

      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        message.error("File size must be less than 5MB");
        setIsPickingDocument(false);
        return;
      }

      const allowedTypes = [
        "image/png",
        "image/jpg",
        "image/jpeg",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        message.error("Only PNG, JPG, JPEG, and PDF files are allowed");
        setIsPickingDocument(false);
        return;
      }

      setValue("utilityBill", file.name);
      setUtilityBillFile(file);
    } catch (error: any) {
      message.error(errorHandler(error) || "Failed to upload file");
    } finally {
      setIsPickingDocument(false);
    }
  };

  const onChangeIdentityDocumentType = async (e: any) => {
    if (isPickingDocument) return;
    setIsPickingDocument(true);

    try {
      const file = e.target.files?.[0];
      if (!file) {
        setIsPickingDocument(false);
        return;
      }

      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        message.error("File size must be less than 5MB");
        setIsPickingDocument(false);
        return;
      }

      const allowedTypes = [
        "image/png",
        "image/jpg",
        "image/jpeg",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        message.error("Only PNG, JPG, JPEG, and PDF files are allowed");
        setIsPickingDocument(false);
        return;
      }

      setValue("idDocument", file.name);
      setIdentityTypeFile(file);
    } catch (error: any) {
      message.error(errorHandler(error) || "Failed to upload file");
    } finally {
      setIsPickingDocument(false);
    }
  };

  const handleAddBusinessOwner = async (values: any) => {
    setIsSubmitting(true);
    const form: any = new FormData();
    form.append(
      "CustomerId",
      formData?.funder_id || resumeCustomerData?.customer_id
    );
    form.append("OwnersInfo[0].FunderPersonType", CustomerPeopleTypes.PARTNER);
    form.append("OwnersInfo[0].FirstName", values?.firstName);
    form.append("OwnersInfo[0].LastName", values?.lastName);
    form.append("OwnersInfo[0].Gender", values?.gender);
    form.append("OwnersInfo[0].CountryCode", values?.countryCode);
    form.append("OwnersInfo[0].City", values?.city);
    form.append("OwnersInfo[0].Address", values?.address);
    form.append("OwnersInfo[0].State", values?.state);
    form.append("OwnersInfo[0].DateOfBirth", values?.dateOfBirth);
    form.append("OwnersInfo[0].GovtIdentityType", values?.govtIdentityType);
    form.append("OwnersInfo[0].UtilityBillType", values?.utilityDocumentType);
    form.append("OwnersInfo[0].IdentityNumber", values?.identityNumber);
    form.append("OwnersInfo[0].Email", values?.email);
    form.append("OwnersInfo[0].PhoneNumber", values?.phone_number);
    form.append(
      "OwnersInfo[0].BusinessType",
      formData?.customerSubType || BusinessTypes.PARTNERSHIP
    );
    form.append(
      "OwnersInfo[0].FunderPersonDocuments[0].DocumentType",
      DocumentType.GOVERNMENT_ISSUED_DOCUMENT.toString()
    );
    form.append(
      "OwnersInfo[0].FunderPersonDocuments[0].Document",
      identityTypeFile
    );
    form.append(
      "OwnersInfo[0].FunderPersonDocuments[1].DocumentType",
      DocumentType.PROOF_OF_ADDRESS.toString()
    );
    form.append(
      "OwnersInfo[0].FunderPersonDocuments[1].Document",
      utilityBillFile
    );

    try {
      const response = await postFormDataWithDotNet(
        `${ajaxEndpoints.ADD_BUSINESS_OWNER_INFORMATION}?sourceTypeId=2`,
        form
      );
      if (response?.statusCode === 200 || response?.status_code === 200) {
        message.success(
          response?.data?.message ||
            response?.message ||
            "Partner added successfully!"
        );
        refetchCustomerPeopleDetails();
        reset();
        // Navigate to next step
        handleProceed(AllPartnershipBusinessSteps.BUSINESS_DOCUMENTS);
      }
    } catch (error: any) {
      message.error(errorHandler(error) || "Failed to add partner");
    }
    setIsSubmitting(false);
  };

  const handleUpdateBusinessOwner = async (values: any) => {
    setIsSubmitting(true);
    const form: any = new FormData();
    form.append(
      "CustomerId",
      formData?.funder_id || resumeCustomerData?.customer_id
    );
    form.append("OwnersInfo[0].Id", selectedBusinessOwner?.id);
    form.append("OwnersInfo[0].FunderPersonType", CustomerPeopleTypes.PARTNER);
    form.append("OwnersInfo[0].FirstName", values?.firstName);
    form.append("OwnersInfo[0].LastName", values?.lastName);
    form.append("OwnersInfo[0].Gender", values?.gender);
    form.append("OwnersInfo[0].CountryCode", values?.countryCode);
    form.append("OwnersInfo[0].City", values?.city);
    form.append("OwnersInfo[0].Address", values?.address);
    form.append("OwnersInfo[0].State", values?.state);
    form.append("OwnersInfo[0].DateOfBirth", values?.dateOfBirth);
    form.append("OwnersInfo[0].GovtIdentityType", values?.govtIdentityType);
    form.append("OwnersInfo[0].UtilityBillType", values?.utilityDocumentType);
    form.append("OwnersInfo[0].IdentityNumber", values?.identityNumber);
    form.append("OwnersInfo[0].Email", values?.email);
    form.append("OwnersInfo[0].PhoneNumber", values?.phoneNumber);
    form.append(
      "OwnersInfo[0].BusinessType",
      formData?.customerSubType || BusinessTypes.PARTNERSHIP
    );
    form.append(
      "OwnersInfo[0].FunderPersonDocuments[0].DocumentType",
      DocumentType.GOVERNMENT_ISSUED_DOCUMENT.toString()
    );
    form.append(
      "OwnersInfo[0].FunderPersonDocuments[0].Document",
      identityTypeFile
    );
    form.append(
      "OwnersInfo[0].FunderPersonDocuments[1].DocumentType",
      DocumentType.PROOF_OF_ADDRESS.toString()
    );
    form.append(
      "OwnersInfo[0].FunderPersonDocuments[1].Document",
      utilityBillFile
    );

    try {
      const response = await putFormDataWithDotNet(
        `${ajaxEndpoints.ADD_BUSINESS_OWNER_INFORMATION}?sourceTypeId=2`,
        form
      );
      if (response?.statusCode === 200 || response?.status_code === 200) {
        message.success(
          response?.data?.message ||
            response?.message ||
            "Partner updated successfully!"
        );
        refetchCustomerPeopleDetails();
        reset();
        setSelectedBusinessOwner(undefined);
      }
    } catch (error: any) {
      message.error(errorHandler(error) || "Failed to update partner");
    }
    setIsSubmitting(false);
  };

  const onSubmit = async (values: any) => {
    if (selectedBusinessOwner?.id) {
      return handleUpdateBusinessOwner(values);
    }
    return handleAddBusinessOwner(values);
  };

  useEffect(() => {
    if (countryCode) {
      setSelectedCountryCode(countryCode);
    }
  }, [countryCode]);

  const handleAddAnother = () => {
    setSelectedBusinessOwner(undefined);
    reset();
  };

  useEffect(() => {
    if (selectedBusinessOwner) {
      setValue("firstName", selectedBusinessOwner?.first_name);
      setValue("lastName", selectedBusinessOwner?.last_name);
      setValue(
        "dateOfBirth",
        selectedBusinessOwner?.date_of_birth?.split("T")[0]
      );
      setValue("countryCode", selectedBusinessOwner?.country_code);
      setValue("identityNumber", selectedBusinessOwner?.identity_number);
      setValue("gender", selectedBusinessOwner?.gender);
      setValue("address", selectedBusinessOwner?.address);
      setValue("city", selectedBusinessOwner?.city);
      setValue("state", selectedBusinessOwner?.state);
      setValue("email", selectedBusinessOwner?.email);
      setValue("phone_number", selectedBusinessOwner?.phone_number);
      setValue(
        "govtIdentityType",
        selectedBusinessOwner?.govt_identity_type_id
      );
      setTimeout(() => {
        setValue(
          "utilityDocumentType",
          selectedBusinessOwner?.utility_bill_type_id
        );
      }, 2000);
    }
  }, [selectedBusinessOwner, setValue]);

  const handleButtonClick = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  return (
    <div>
      <>
        {isLoadingBusinessOwner ? (
          <Loader />
        ) : (
          <>
            {customerPeopleDetails && customerPeopleDetails.length > 0 && (
              <BusinessOwnersList
                list={customerPeopleDetails}
                setSelectedBusinessOwner={setSelectedBusinessOwner}
              />
            )}
          </>
        )}
      </>
      <div className="mt-5">
        <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
          <div className="mt-4 row">
            <div className="col-12">
              <CustomInputField
                type="text"
                label="First Name"
                maxLength={128}
                reference={register}
                name="firstName"
                placeholder="First Name"
                defaultValue={selectedBusinessOwner?.first_name}
              />
              <ErrorHandler errors={errors?.firstName} />
            </div>
            <div className="col-12">
              <CustomInputField
                type="text"
                label="Last Name"
                maxLength={128}
                reference={register}
                name="lastName"
                placeholder="Last Name"
                defaultValue={selectedBusinessOwner?.last_name}
              />
              <ErrorHandler errors={errors?.lastName} />
            </div>
            <div className="col-12">
              <CustomInputField
                type="email"
                placeholder="morty@ricknmorty.com"
                label="Email Address"
                reference={register}
                name="email"
                defaultValue={selectedBusinessOwner?.email}
              />
              <ErrorHandler errors={errors.email} />
            </div>

            <div className="col-12">
              <CustomInputField
                type="text"
                maxLength={20}
                reference={register}
                label="Phone Number"
                placeholder="07000000000"
                name="phone_number"
                defaultValue={selectedBusinessOwner?.phone_number}
              />
              <ErrorHandler errors={errors.phone_number} />
            </div>
            <div className="col-12">
              <CustomInputField
                type="date"
                label="Date of birth"
                reference={register}
                name="dateOfBirth"
                defaultValue={
                  selectedBusinessOwner?.date_of_birth?.split("T")[0]
                }
                max={getMaxDateOfBirth()}
              />
              <ErrorHandler errors={errors?.dateOfBirth} />
            </div>
            <div className="col-12 mt-n3">
              <div className="form-group">
                <label>
                  Nationality{" "}
                  {isLoadingCountries && <small>(Loading...)</small>}
                </label>
                <select
                  name="countryCode"
                  className="form-control"
                  ref={register}
                  defaultValue={selectedBusinessOwner?.country_code}
                >
                  <option value="">Select country</option>
                  {countries &&
                    countries?.map(
                      ({ name, iso2 }: { name: string; iso2: string }) => (
                        <option value={iso2} key={iso2}>
                          {name}
                        </option>
                      )
                    )}
                </select>
                <ErrorHandler errors={errors.countryCode} />
              </div>
            </div>
            <div className="col-12 mt-4">
              <CustomInputField
                type="text"
                label="Identity Number"
                maxLength={128}
                reference={register}
                name="identityNumber"
                placeholder="Enter identity number"
                defaultValue={selectedBusinessOwner?.identity_number}
              />
              <ErrorHandler errors={errors.identityNumber} />
            </div>
            <div className="col-12 mt-n3">
              <div className="form-group">
                <label>Gender</label>
                <select
                  name="gender"
                  className="form-control"
                  ref={register}
                  defaultValue={selectedBusinessOwner?.gender}
                >
                  <option value="">Gender</option>
                  {genders &&
                    genders?.map(
                      ({ name, id }: { name: string; id: number }) => (
                        <option value={name} key={id}>
                          {name}
                        </option>
                      )
                    )}
                </select>
                <ErrorHandler errors={errors.gender} />
              </div>
            </div>

            <div className="col-12 mt-4">
              <CustomInputField
                type="text"
                label="Residential Address"
                maxLength={128}
                reference={register}
                name="address"
                placeholder="Enter your residential address"
                defaultValue={selectedBusinessOwner?.address}
              />
              <ErrorHandler errors={errors.address} />
            </div>
            <div className="col-12">
              <CustomInputField
                type="text"
                label="City"
                maxLength={128}
                reference={register}
                name="city"
                placeholder="City"
                defaultValue={selectedBusinessOwner?.city}
              />
              <ErrorHandler errors={errors.city} />
            </div>
            <div className="col-12 mt-n3">
              <div className="form-group">
                <label>
                  State of Residence{" "}
                  {isStatesLoading && <small>(Loading...)</small>}
                </label>
                <select
                  name="state"
                  className="form-control"
                  ref={register}
                  defaultValue={selectedBusinessOwner?.state}
                >
                  <option value="">State</option>
                  {states?.map(({ name, id }: { name: string; id: number }) => (
                    <option value={name} key={id}>
                      {name}
                    </option>
                  ))}
                </select>
                <ErrorHandler errors={errors.state} />
              </div>
            </div>

            <div className="col-12 mt-4">
              <div className="form-group">
                <label>Identification Type</label>
                <select
                  name="govtIdentityType"
                  className="form-control"
                  ref={register}
                  defaultValue={selectedBusinessOwner?.govt_identity_type_id}
                >
                  <option value="">Select identification type</option>
                  {identityTypes &&
                    identityTypes?.map(
                      ({ name, id }: { name: string; id: number }) => (
                        <option value={id} key={id}>
                          {name}
                        </option>
                      )
                    )}
                </select>
                <ErrorHandler errors={errors.govtIdentityType} />
              </div>
            </div>

            <div className="col-12 mt-4">
              <CustomInputField
                type="text"
                maxLength={128}
                placeholder=""
                label="Upload ID"
                reference={register}
                name="idDocument"
                readOnly
              />

              <button
                type="button"
                className="btn btn-sm advancly-btn mb-3"
                onClick={() => idDocumenTypeRef?.current?.click()}
              >
                {identityTypeFile ? "Replace" : "Choose File"}
              </button>

              <ErrorHandler errors={errors.idDocument} />
              <input
                type="file"
                className="d-none"
                ref={idDocumenTypeRef}
                onChange={onChangeIdentityDocumentType}
                accept="image/png, image/jpg, image/jpeg, application/pdf"
              />
            </div>

            <div className="col-12 mt-n3">
              <div className="form-group">
                <label>
                  Utility Bill Type{" "}
                  {isLoadingUtilityBillType && <small>(Loading...)</small>}
                </label>
                <select
                  name="utilityDocumentType"
                  className="form-control"
                  ref={register}
                  defaultValue={selectedBusinessOwner?.utility_bill_type_id}
                >
                  <option value="">Select</option>
                  {utilityBillTypes?.map(
                    ({ name, id }: { name: string; id: number }) => (
                      <option value={id} key={id}>
                        {name}
                      </option>
                    )
                  )}
                </select>
                <ErrorHandler errors={errors.utilityDocumentType} />
              </div>
            </div>

            <div className="col-12 mt-4">
              <CustomInputField
                type="text"
                maxLength={128}
                placeholder=""
                label="Proof of Address (e.g. Utility Bill)"
                reference={register}
                name="utilityBill"
                readOnly
              />

              <button
                type="button"
                className="btn btn-sm advancly-btn mb-3"
                onClick={() => UtilityFileInputRef?.current?.click()}
              >
                {utilityBillFile ? "Replace" : "Choose File"}
              </button>

              <ErrorHandler errors={errors.utilityBill} />
              <input
                type="file"
                className="d-none"
                ref={UtilityFileInputRef}
                onChange={onChangeUtilityBill}
                accept="image/png, image/jpg, image/jpeg, application/pdf"
              />
            </div>
          </div>

          <p className={styles.AddBusinessOwner} onClick={handleAddAnother}>
            Add another Partner
          </p>

          <div className={styles.ActionButtonContainer}>
            <button
              type="submit"
              className={styles.ActionButton}
              disabled={isSubmitting}
              onClick={handleButtonClick}
            >
              {isSubmitting
                ? selectedBusinessOwner?.id
                  ? "Updating..."
                  : "Saving..."
                : selectedBusinessOwner?.id
                  ? "Update"
                  : "Save"}
            </button>
            <Button
              size="large"
              className={styles.NextButton}
              onClick={() =>
                handleProceed(AllPartnershipBusinessSteps.BUSINESS_DOCUMENTS)
              }
            >
              Next
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBusinessOwner;
